import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CookieConsentService } from './cookie-consent.service';

/** Preferenze cookie per categoria (i necessari sono sempre attivi). */
interface CookieConsent {
	necessary: true;
	functional: boolean;
	analytics: boolean;
	performance: boolean;
	ads: boolean;
	/** Timestamp (ms) del salvataggio, per calcolare la scadenza. */
	ts: number;
}

/** Chiave localStorage e durata del consenso (1 anno, come da banner GDPR). */
const STORAGE_KEY = 'kcs-cookie-consent';
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

@Component({
	standalone: true,
	selector: 'app-cookie-consent',
	imports: [CommonModule, FormsModule, ButtonModule, DialogModule, DividerModule, ToggleSwitchModule],
	templateUrl: './cookie-consent.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./cookie-consent.component.scss'],
})
export class CookieConsentComponent implements OnInit, OnDestroy {
	/** Banner in basso: visibile solo finché l'utente non ha espresso una scelta. */
	bannerVisible = false;
	/** Modal "Personalizza" con il dettaglio delle categorie. */
	prefsVisible = false;

	/** Preferenze modificabili nel modal (i necessari non sono modificabili). */
	prefs = { functional: false, analytics: false, performance: false, ads: false };

	private sub?: Subscription;

	constructor(private readonly cookieConsent: CookieConsentService) {}

	ngOnInit(): void {
		const saved = this.readConsent();
		if (saved) {
			this.prefs = { functional: saved.functional, analytics: saved.analytics, performance: saved.performance, ads: saved.ads };
		} else {
			this.bannerVisible = true;
		}
		// Riapertura del modal richiesta da altri punti dell'app (es. link nel footer).
		this.sub = this.cookieConsent.open$.subscribe(() => this.openPreferences());
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}

	/** "Accetta" nel banner o nel modal: abilita tutte le categorie. */
	acceptAll(): void {
		this.prefs = { functional: true, analytics: true, performance: true, ads: true };
		this.persist();
	}

	/** "Rifiuta": mantiene solo i cookie necessari. */
	rejectAll(): void {
		this.prefs = { functional: false, analytics: false, performance: false, ads: false };
		this.persist();
	}

	/** "Salva preferenze": memorizza le scelte correnti dei toggle. */
	savePreferences(): void {
		this.persist();
	}

	/** Apre il modal di dettaglio (dal banner o dal pulsante flottante "Impostazioni"). */
	openPreferences(): void {
		this.prefsVisible = true;
	}

	private persist(): void {
		const consent: CookieConsent = { necessary: true, ...this.prefs, ts: Date.now() };
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
		} catch {
			// Storage non disponibile (es. navigazione privata): il consenso resta valido per la sessione.
		}
		this.bannerVisible = false;
		this.prefsVisible = false;
	}

	private readConsent(): CookieConsent | null {
		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			if (!raw) return null;
			const parsed = JSON.parse(raw) as CookieConsent;
			if (!parsed?.ts || Date.now() - parsed.ts > CONSENT_TTL_MS) return null;
			return parsed;
		} catch {
			return null;
		}
	}
}
